/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useLocation, useNavigate } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { Tag, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import type { ApiStatus } from '@coursera/coursera-ui/lib/constants/sharedConstants';
import { API_BEFORE_SEND, API_ERROR, API_IN_PROGRESS } from '@coursera/coursera-ui/lib/constants/sharedConstants';

import PriceCTA from 'bundles/coursera-plus-landing-page/components/pricePresentation/PriceCTA';
import PriceWithSuperScriptCurrency from 'bundles/coursera-plus-landing-page/components/pricePresentation/PriceWithSuperScriptCurrency';
import type { SubscriptionTiersProductPrice } from 'bundles/coursera-plus-landing-page/contentfulData/LandingPageDataProvider';
import { useLandingPageExtended } from 'bundles/coursera-plus-landing-page/contentfulData/useLandingPageData';
import type { previewCourseraPlusAnnualWithFreeTrialTestVariant } from 'bundles/coursera-plus-landing-page/utils/courseraPlusAnnualWithFreeTrial/annualWithFreeTrialUtils';
import type { CourseraPlusProductAndPrice } from 'bundles/coursera-plus-landing-page/utils/courseraPlusAnnualWithFreeTrial/useGetCourseraPlusAnnualWithFreeTrialProduct';
import { useGetCourseraPlusAnnualWithFreeTrialProduct } from 'bundles/coursera-plus-landing-page/utils/courseraPlusAnnualWithFreeTrial/useGetCourseraPlusAnnualWithFreeTrialProduct';
import { productPriceToMonetaryAmount } from 'bundles/coursera-plus-landing-page/utils/productPriceToMonetaryAmount';
import IdealUpgradeEnrollModal from 'bundles/coursera-plus/components/subscriptionTiers/upgradeToPlus/IdealUpgradeEnrollModal';
import type { SubscriptionTiersProductVariant } from 'bundles/coursera-plus/constants/CourseraPlusProductVariant';
import { isAnnualByVariantId } from 'bundles/coursera-plus/constants/CourseraPlusProductVariant';
import { SUBSCRIBE_TO_COURSERA_PLUS } from 'bundles/enroll-course/common/EnrollmentChoiceTypes';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-course/lib/enrollmentChoiceUtils';
import type { SubmitEnrollmentPromiseOptions } from 'bundles/enroll-course/lib/enrollmentChoiceUtils';
import EnrollErrorModal from 'bundles/enroll/components/common/EnrollErrorModal';
import { ENROLL } from 'bundles/enroll/utils/enrollActionParams';
import type { ApiError } from 'bundles/enroll/utils/errorUtils';
import { ANNUAL, MONTHLY } from 'bundles/subscriptions/common/BillingCycleType';
import { SIGNUP_FORM_KEY } from 'bundles/user-account/common/constants';

import _t from 'i18n!nls/coursera-plus-landing-page';

const styles = {
  priceCtaContainer: css`
    display: flex;
    gap: var(--cds-spacing-300);

    @media (max-width: 375px) {
      align-items: center;
    }

    @media (max-width: 818px) {
      flex-direction: column;
    }
  `,
  annualTitle: css`
    margin-right: 16px;
  `,
  tag: css`
    border-radius: 4px;
    border: 1px solid #e3edff !important;
    background: #e3edff !important;
  `,
  bestValueTag: css`
    position: absolute;
    top: -10px;
    background: #fff;
    border-color: var(--cds-color-grey-300, #a7b4c9) !important;
  `,
  variant3MonthlyPriceContainer: css`
    margin: 24px 0 12px;
  `,
  variant3AnnualPriceContainer: css`
    margin-bottom: 7px;
  `,
  annualTitleContainer: (withMarginBottom: boolean = false) => css`
    display: flex;
    align-items: center;

    ${withMarginBottom ? `margin-bottom: var(--cds-spacing-100)` : ''}
  `,
  subTitleContainer: css`
    padding: 16px 0;
  `,
  billingCadence: css`
    margin-bottom: var(--cds-spacing-200);
  `,
};

type CtaCopyArgs = {
  epicVariant: ReturnType<typeof previewCourseraPlusAnnualWithFreeTrialTestVariant>;
  monthlyPriceDisplay?: JSX.Element | null;
  annualDiscountPercent?: number | null;
  isCourseraLiteOwner: boolean;
  annualPriceAsMonthlyDisplay?: JSX.Element | null;
  courseraPlusAnnualProductAndPrice: CourseraPlusProductAndPrice;
  courseraPlusMonthlyPrice?: SubscriptionTiersProductPrice;
  courseraPlusAnnualPrice?: SubscriptionTiersProductPrice;
  annualAmountAsMonthly?: number;
  monthlyCtaLoading: boolean;
  annualCtaLoading: boolean;
  hasFreeTrial: boolean;
  isMobile: boolean;
};

type ButtonType = ButtonProps['variant'];

type CtaConfigType = {
  title: JSX.Element;
  subTitle: JSX.Element;
  ctaLabel: string;
  customStyles: {
    borderColor?: string;
    backgroundColor?: string;
    position?: string;
  };
  trackingName: string;
  buttonType?: ButtonType;
  productItemId?: string | null;
  loading?: boolean;
};

const getCtaCopyAndConfigs = ({
  epicVariant,
  annualAmountAsMonthly,
  annualDiscountPercent,
  isCourseraLiteOwner,
  courseraPlusAnnualProductAndPrice,
  courseraPlusMonthlyPrice,
  monthlyCtaLoading,
  annualCtaLoading,
  hasFreeTrial,
  monthlyPriceDisplay,
  annualPriceAsMonthlyDisplay,
  isMobile,
}: CtaCopyArgs):
  | {
      primaryCtaConfig?: CtaConfigType;
      secondaryCtaConfig?: CtaConfigType;
    }
  | undefined => {
  let primaryCtaConfig;
  let secondaryCtaConfig;
  const monthlyProductItemId = courseraPlusMonthlyPrice?.productItemId;
  const annualProductItemId = courseraPlusAnnualProductAndPrice?.productItemId;
  const annualCurrencyCode = courseraPlusAnnualProductAndPrice?.price?.currencyCode;

  let monthlyCTALabel = hasFreeTrial ? _t('Start 7-day free trial') : _t('Renew subscription');
  let yearlyCTALabel = hasFreeTrial ? _t('Start 7-day free trial') : _t('Renew subscription');
  if (isCourseraLiteOwner) {
    monthlyCTALabel = isMobile ? _t('Upgrade to monthly') : _t('Upgrade to Plus monthly');
    yearlyCTALabel = isMobile ? _t('Upgrade to annual') : _t('Upgrade to Plus annual');
  }
  const annualDiscountPercentCopy = _t('Save #{annualDiscountPercent}%', { annualDiscountPercent });

  if (epicVariant === '2') {
    primaryCtaConfig = {
      title: (
        <div css={styles.annualTitleContainer(false)}>
          <Tag
            color="default"
            priority="secondary"
            variant="category"
            aria-label={_t('Best Value')}
            css={styles.bestValueTag}
          >
            {_t('Best Value')}
          </Tag>
          <Typography2 variant="titleSmall" component="span" css={styles.annualTitle}>
            {_t('Annual')}
          </Typography2>
          <Tag
            variant="status"
            priority="secondary"
            aria-label={annualDiscountPercentCopy}
            color="highlight1"
            css={styles.tag}
          >
            {annualDiscountPercentCopy}
          </Tag>
        </div>
      ),
      subTitle: (
        <>
          <Typography2 variant="bodyPrimary" component="div" css={styles.subTitleContainer}>
            <FormattedMessage
              message={_t('Less than {price}/mo, billed annually')}
              price={annualPriceAsMonthlyDisplay}
            />
          </Typography2>
        </>
      ),
      ctaLabel: yearlyCTALabel,
      trackingName: 'annual_with_free_trial_annual_subscribe_button',
      customStyles: {
        backgroundColor: '#F2F5FA',
        position: 'relative',
      },
      buttonType: 'primary' as ButtonType,
      productItemId: annualProductItemId,
      loading: annualCtaLoading,
    };
    secondaryCtaConfig = {
      title: (
        <Typography2 variant="titleSmall" component="span">
          {_t('Monthly')}
        </Typography2>
      ),
      subTitle: (
        <>
          <Typography2 variant="bodyPrimary" component="div" css={styles.subTitleContainer}>
            <FormattedMessage message={_t('{price} billed monthly')} price={monthlyPriceDisplay} />
          </Typography2>
        </>
      ),
      ctaLabel: monthlyCTALabel,
      trackingName: 'annual_with_free_trial_monthly_subscribe_button',
      customStyles: {
        borderColor: '#E8EEF7',
      },
      productItemId: monthlyProductItemId,
      loading: monthlyCtaLoading,
    };
  }

  if (epicVariant === '3') {
    const showSuperscriptCurrencyFormatForAnnual =
      courseraPlusAnnualProductAndPrice && annualAmountAsMonthly && annualCurrencyCode;
    primaryCtaConfig = {
      title: (
        <div css={styles.annualTitleContainer(true)}>
          <Typography2 variant="titleSmall" component="span" css={styles.annualTitle}>
            {_t('Annual')}
          </Typography2>
          <Tag
            variant="status"
            priority="secondary"
            aria-label={annualDiscountPercentCopy}
            color="highlight1"
            css={styles.tag}
          >
            {annualDiscountPercentCopy}
          </Tag>
        </div>
      ),
      subTitle: (
        <>
          <Typography2 variant="bodySecondary" component="div">
            <FormattedMessage message={_t('Less than')} />
          </Typography2>
          <div css={styles.variant3AnnualPriceContainer}>
            {showSuperscriptCurrencyFormatForAnnual && (
              <PriceWithSuperScriptCurrency
                amount={annualAmountAsMonthly}
                currencyCode={annualCurrencyCode}
                subscriptionCadence={_t('/mo')}
              />
            )}
          </div>
          <Typography2 css={styles.billingCadence} variant="bodySecondary" component="div">
            {_t('Billed annually')}
          </Typography2>
        </>
      ),
      ctaLabel: yearlyCTALabel,
      trackingName: 'annual_with_free_trial_annual_subscribe_button',
      customStyles: {
        backgroundColor: '#F2F5FA',
      },
      buttonType: 'primary' as ButtonType,
      productItemId: annualProductItemId,
      loading: annualCtaLoading,
    };
    secondaryCtaConfig = {
      title: (
        <Typography2 variant="titleSmall" component="span">
          {_t('Monthly')}
        </Typography2>
      ),
      subTitle: (
        <>
          <div css={styles.variant3MonthlyPriceContainer}>
            {courseraPlusMonthlyPrice && (
              <PriceWithSuperScriptCurrency
                amount={courseraPlusMonthlyPrice.amount as number}
                currencyCode={courseraPlusMonthlyPrice.currencyCode as string}
                subscriptionCadence={_t('/mo')}
              />
            )}
          </div>
          <Typography2 css={styles.billingCadence} variant="bodySecondary" component="div">
            {_t('Billed monthly')}
          </Typography2>
        </>
      ),
      ctaLabel: monthlyCTALabel,
      trackingName: 'annual_with_free_trial_monthly_subscribe_button',
      customStyles: {
        borderColor: '#E8EEF7',
      },
      productItemId: monthlyProductItemId,
      loading: monthlyCtaLoading,
    };
  }

  return {
    primaryCtaConfig,
    secondaryCtaConfig,
  };
};

type Props = {
  isCourseraLiteOwner: boolean;
};

const CourseraPlusAnnualWithFreeTrialContainer = ({ isCourseraLiteOwner }: Props) => {
  const isMobile = useMediaQuery(breakpoints.only('xs'));
  const [apiStatus, setApiStatus] = useState<ApiStatus>(API_BEFORE_SEND);
  const [error, setError] = useState<ApiError | undefined>();
  const [selectedProductItemId, setSelectedProductItemId] = useState<string | undefined>();
  const { courseraPlusAnnualWithFreeTrialEpicVariant } = useLandingPageExtended();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const {
    monthlyPriceDisplay,
    courseraPlusMonthlyPrice,
    annualPriceAsMonthlyDisplay,
    annualDiscountPercent,
    annualAmountAsMonthly,
    courseraPlusAnnualProductAndPrice,
    hasFreeTrial,
  } = useGetCourseraPlusAnnualWithFreeTrialProduct();

  const handleError = (apiError?: ApiError) => {
    setError(apiError);
    setApiStatus(API_ERROR);
  };

  const checkoutSubscription = useCallback<(productItemId?: string) => void>(
    (productItemId?: string) => {
      if (apiStatus === API_IN_PROGRESS) {
        return;
      }

      setSelectedProductItemId(productItemId);
      if (isCourseraLiteOwner) {
        return;
      }
      setApiStatus(API_IN_PROGRESS);

      const handleSubmitPromise = choiceTypeToHandleSubmitPromise[SUBSCRIBE_TO_COURSERA_PLUS];
      const options: SubmitEnrollmentPromiseOptions = {
        isSubscriptionOnly: true,
        productItemId,
      };
      submitEnrollmentPromise({ handleSubmitPromise, options }).catch(handleError);
    },
    [apiStatus, isCourseraLiteOwner]
  );

  if (!monthlyPriceDisplay || !annualPriceAsMonthlyDisplay || !annualAmountAsMonthly) {
    return null;
  }

  const config = getCtaCopyAndConfigs({
    annualDiscountPercent,
    epicVariant: courseraPlusAnnualWithFreeTrialEpicVariant,
    annualAmountAsMonthly,
    annualPriceAsMonthlyDisplay,
    monthlyPriceDisplay,
    isCourseraLiteOwner,
    courseraPlusMonthlyPrice,
    courseraPlusAnnualProductAndPrice,
    monthlyCtaLoading:
      apiStatus === API_IN_PROGRESS && selectedProductItemId === courseraPlusMonthlyPrice?.productItemId,
    annualCtaLoading:
      apiStatus === API_IN_PROGRESS && selectedProductItemId === courseraPlusMonthlyPrice?.productItemId,
    hasFreeTrial,
    isMobile,
  });

  const handleErrorModalClose = () => {
    setApiStatus(API_BEFORE_SEND);
    setSelectedProductItemId(undefined);
    setError(undefined);
  };

  const onClick = (productItemId?: string) => {
    if (!isAuthenticatedUser) {
      navigate(
        {
          pathname,
          search: `?action=${ENROLL}&authMode=${SIGNUP_FORM_KEY}&variant=${productItemId}`,
        },
        { replace: true }
      );
    } else {
      checkoutSubscription(productItemId);
    }
  };

  return (
    <>
      {selectedProductItemId && (
        <IdealUpgradeEnrollModal
          courseraPlusProductPrice={
            isAnnualByVariantId(selectedProductItemId as SubscriptionTiersProductVariant)
              ? courseraPlusAnnualProductAndPrice.price
              : productPriceToMonetaryAmount(courseraPlusMonthlyPrice)
          }
          onClose={() => setSelectedProductItemId(undefined)}
          isStandaloneCourseraPlusUpgrade={true}
          toTierBillingCycle={
            isAnnualByVariantId(selectedProductItemId as SubscriptionTiersProductVariant) ? ANNUAL : MONTHLY
          }
        />
      )}
      <div css={styles.priceCtaContainer}>
        <PriceCTA disabled={apiStatus === API_IN_PROGRESS} onClick={onClick} config={config?.primaryCtaConfig} />
        <PriceCTA disabled={apiStatus === API_IN_PROGRESS} onClick={onClick} config={config?.secondaryCtaConfig} />
      </div>

      {apiStatus === API_ERROR && <EnrollErrorModal onClose={handleErrorModalClose} error={error} />}
    </>
  );
};

export default CourseraPlusAnnualWithFreeTrialContainer;
